import type { ReactNode } from "react";

import { Button } from "../components/Button";
import { Card } from "../components/Display";
import { Icon, type IconName } from "../components/Icon";
import { Spinner } from "../components/Spinner";
import { cn } from "../lib/cn";

// ── PageHeader ───────────────────────────────────────────────────────

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Buttons on the right (primary action last). */
  actions?: ReactNode;
  /** Breadcrumb or back link above the title. */
  eyebrow?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, actions, eyebrow, className }: PageHeaderProps) {
  return (
    <header
      className={cn(
        "mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        {eyebrow && <div className="text-sm text-muted-foreground">{eyebrow}</div>}
        <h1 className="text-2xl font-bold tracking-tight sm:text-[1.7rem]">{title}</h1>
        {description && <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

// ── EmptyState / ErrorState / LoadingState ───────────────────────────

export interface EmptyStateProps {
  icon?: IconName;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon = "inbox",
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center gap-3 px-6 py-14 text-center", className)}>
      <span className="flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand-soft-foreground">
        <Icon name={icon} size={26} />
      </span>
      <div className="space-y-1">
        <p className="font-semibold">{title}</p>
        {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  title: ReactNode;
  description?: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
  /** Request id from the API error, shown so support can find the log line. */
  reference?: string | null;
  referenceLabel?: string;
  className?: string;
}

export function ErrorState({
  title,
  description,
  retryLabel = "Try again",
  onRetry,
  reference,
  referenceLabel = "Reference",
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn("flex flex-col items-center gap-3 px-6 py-14 text-center", className)}
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-danger-soft text-danger">
        <Icon name="circle-alert" size={26} />
      </span>
      <div className="space-y-1">
        <p className="font-semibold">{title}</p>
        {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
        {reference && (
          <p className="font-mono text-xs text-muted-foreground">
            {referenceLabel}: {reference}
          </p>
        )}
      </div>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          <Icon name="refresh" size={16} />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export function LoadingState({
  label = "Loading",
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-center py-16 text-muted-foreground", className)}>
      <Spinner size={28} label={label} />
    </div>
  );
}

/** Full-screen splash while the app checks the session. */
export function LoadingScreen({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas">
      <Spinner size={32} label={label} />
    </div>
  );
}

// ── KpiTile ──────────────────────────────────────────────────────────

export interface KpiTileProps {
  label: ReactNode;
  value: ReactNode;
  icon?: IconName;
  /** e.g. "+12% vs last week" or a muted hint when no data yet. */
  hint?: ReactNode;
  tone?: "light" | "dark";
  className?: string;
}

/** Dashboard tile; `dark` is the ink card with a yellow accent from the MOTOLYNK app. */
export function KpiTile({ label, value, icon, hint, tone = "light", className }: KpiTileProps) {
  const dark = tone === "dark";
  return (
    <Card className={cn("p-5", dark && "border-ink-border bg-ink text-ink-foreground", className)}>
      <div className="flex items-start justify-between gap-3">
        <p
          className={cn(
            "text-sm font-medium",
            dark ? "text-ink-foreground/70" : "text-muted-foreground",
          )}
        >
          {label}
        </p>
        {icon && (
          <span
            className={cn(
              "flex size-9 items-center justify-center rounded-full",
              dark ? "bg-brand text-brand-foreground" : "bg-brand-soft text-brand-soft-foreground",
            )}
          >
            <Icon name={icon} size={18} />
          </span>
        )}
      </div>
      <p className="mt-3 font-display text-3xl font-bold tracking-tight">{value}</p>
      {hint && (
        <p
          className={cn("mt-1 text-xs", dark ? "text-ink-foreground/60" : "text-muted-foreground")}
        >
          {hint}
        </p>
      )}
    </Card>
  );
}
